// Persistencia local: todo queda en el navegador. Cada acceso se protege porque el almacenamiento
// puede no existir o estar bloqueado (ventanas privadas, datos de sitio bloqueados).
import { DEFAULT_LAUNCH, type LaunchConditions, type Motor } from '@openrocket-web/physics';
import type { UnitSystem } from '@openrocket-web/physics';

export interface WindSettings {
  readonly speed: number;
  /** Dirección de la que sopla (rad desde el norte). */
  readonly direction: number;
  readonly turbulenceIntensity: number;
}

export interface SimSettings {
  readonly roughness: number;
  readonly timeStep: number;
  readonly seed: number;
  readonly perturbation: boolean;
}

/** Definición de una simulación de la lista (sin resultados: esos no se guardan). */
export interface SimDef {
  readonly id: string;
  readonly name: string;
  readonly launch: LaunchConditions;
  readonly wind: WindSettings;
  readonly sim: SimSettings;
  /** Configuración de motor con la que se simula; sin ella, la activa. */
  readonly configId?: string;
}

export type Theme = 'light' | 'dark';

/** Lo único que se conserva entre sesiones: el diseño siempre arranca vacío. */
export interface Prefs {
  readonly units: UnitSystem;
  readonly motors: readonly Motor[];
  readonly theme?: Theme;
}

const KEY = 'simulador-cohetes:v1';

export const DEFAULT_WIND: WindSettings = { speed: 0, direction: 0, turbulenceIntensity: 0 };
export const DEFAULT_SIM: SimSettings = { roughness: 10e-6, timeStep: 0.05, seed: 1, perturbation: true };

export function save(prefs: Prefs): boolean {
  try {
    localStorage.setItem(KEY, JSON.stringify({ version: 3, units: prefs.units, motors: prefs.motors, theme: prefs.theme }));
    return true;
  } catch {
    return false;
  }
}

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

export const defaultSim = (id: string, name: string): SimDef => ({ id, name, launch: { ...DEFAULT_LAUNCH, rodLength: 1 }, wind: DEFAULT_WIND, sim: DEFAULT_SIM });

/** Restaura las preferencias guardadas; un diseño guardado por versiones anteriores se ignora (sin borrarlo). Devuelve `undefined` si no hay nada válido. */
export function load(): Partial<Prefs> | undefined {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw === null) return undefined;
    const doc: unknown = JSON.parse(raw);
    if (!isObj(doc) || (doc['version'] !== 1 && doc['version'] !== 2 && doc['version'] !== 3)) return undefined;
    const out: { -readonly [K in keyof Prefs]?: Prefs[K] } = {};
    if (doc['theme'] === 'light' || doc['theme'] === 'dark') out.theme = doc['theme'];
    if (doc['units'] === 'metric' || doc['units'] === 'imperial') out.units = doc['units'];
    if (Array.isArray(doc['motors'])) out.motors = doc['motors'] as Motor[];
    return out;
  } catch {
    return undefined;
  }
}

export function clear(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // nada que borrar
  }
}
